import React from 'react'
import ReactDOM from 'react-dom/client'
import type { ApprovalMapLabels, MapTab } from './ApprovalMapWidget'
import { initAnalytics } from '../lib/analytics'
import ApprovalMapWidget from './ApprovalMapWidget'
import type { WorldData } from './availability'
import styles from './styles.css?inline'

// Shared implementation behind both SDK entry points. Same partner-facing
// pattern as OddsWidget / DistributionWidget / ApprovalsWidget (script tag +
// init()); separate UMD bundle so partners embedding only the map don't pay for
// the other widgets.
//
// The one entry, sdk.ts, calls this as createApprovalMapSdk(WORLD) — all four
// tabs. (Until 2026-07-29 a second staging entry called it bare, rendering the
// USA map alone, while the tabs were under review.)
//
// The world dataset is a parameter rather than an import so an entry that
// omits it never references worldMapPaths and its ~110 KB of geometry is tree-shaken
// out of approval-map-widget.umd.js.
//
// Usage on partner site:
//
//   <script src="https://grid.altsportsdata.com/approval-map-widget.umd.js"></script>
//   <div id="ad-approval-map"></div>
//   <script>
//     ApprovalMapWidget.init('#ad-approval-map', {
//       sport: 'motogp',
//       apiBase: 'https://grid.altsportsdata.com'
//     })
//   </script>
//
// No-data contract for the NXTbets loader: when the sport has no approvals
// (unknown slug, zero approved states, fetch failure) the widget hides its
// own root (display:none) and stamps data-asd-empty="true" on the marker so
// the loader can also hide the surrounding section + nav link.
//
// Theme contract: theme init option > closest('[data-asd-theme]') ancestor
// value > light default.

export interface ApprovalMapWidgetConfig {
  /**
   * Sport slug, or the reserved value 'general' for the operator-level map:
   * where sportsbooks, DFS apps and prediction markets operate at all, with
   * no sport-approval gate. Served by its own API route
   * (GET /api/v1/approval-maps/general); the widget itself is unchanged.
   * One of sport / sportsbook is required.
   */
  sport?: string
  /**
   * Sportsbook slug (slugified name, e.g. 'draftkings') — the book's own
   * reach: its live states / countries painted in its brand colour, with
   * each place's popover listing the sports it carries that are available
   * there (GET /api/v1/sportsbooks/:slug/approval-map). Legend and popover
   * headings switch to sportsbook wording unless `labels` overrides them.
   */
  sportsbook?: string
  apiBase?: string
  theme?: 'light' | 'dark'
  labels?: Partial<ApprovalMapLabels>
  /**
   * Pins the opening tab, bypassing the geo lookup. Only meaningful in the
   * three-tab build; the USA-only bundle ignores it.
   */
  defaultTab?: MapTab
  /**
   * Sub-affiliate embed flag — forwarded as ?aff= so popover tiles link to
   * the sub-affiliate's own per-book URLs; books they have no link for
   * render unlinked.
   */
  subAffiliate?: string
}

export interface ApprovalMapWidgetInstance {
  destroy: () => void
}

const DEFAULT_API_BASE = 'https://grid.altsportsdata.com'

// Inject Poppins from Google Fonts into the host document the first time
// any widget mounts. @font-face declarations don't cross shadow boundaries,
// so the font has to be registered on the page itself for :host to use it.
const POPPINS_HREF = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap'
function ensurePoppinsLoaded(): void {
  if (typeof document === 'undefined') return
  if (document.querySelector(`link[href="${POPPINS_HREF}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = POPPINS_HREF
  document.head.appendChild(link)
}

function resolveTheme(el: HTMLElement, config: ApprovalMapWidgetConfig): 'light' | 'dark' {
  if (config.theme === 'dark' || config.theme === 'light') return config.theme
  const ancestor = el.closest('[data-asd-theme]')
  return ancestor?.getAttribute('data-asd-theme') === 'dark' ? 'dark' : 'light'
}

export class ApprovalMapWidgetSDK {
  private instances = new Map<HTMLElement, { root: ReactDOM.Root, mountPoint: HTMLElement }>()

  constructor(private readonly world?: WorldData) {}

  init(container: string | HTMLElement, config: ApprovalMapWidgetConfig): ApprovalMapWidgetInstance {
    const el = typeof container === 'string'
      ? document.querySelector(container) as HTMLElement | null
      : container

    if (!el) {
      throw new Error(`ApprovalMapWidget.init: container not found (${String(container)})`)
    }

    ensurePoppinsLoaded()
    initAnalytics()

    // Re-init is idempotent: unmount the previous React tree and reuse the
    // same mount point instead of appending a second one. A marker hidden by
    // a prior empty result is un-hidden so the fresh fetch decides again.
    const existing = this.instances.get(el)
    if (existing) {
      existing.root.unmount()
      this.instances.delete(el)
    }
    el.style.removeProperty('display')
    el.removeAttribute('data-asd-empty')

    const shadow = el.shadowRoot ?? el.attachShadow({ mode: 'open' })

    if (!shadow.querySelector('style')) {
      const styleEl = document.createElement('style')
      styleEl.textContent = styles
      shadow.appendChild(styleEl)
    }

    let mountPoint = existing?.mountPoint
    if (!mountPoint || mountPoint.getRootNode() !== shadow) {
      mountPoint = document.createElement('div')
      shadow.appendChild(mountPoint)
    }

    const root = ReactDOM.createRoot(mountPoint)
    root.render(React.createElement(ApprovalMapWidget, {
      sport: config.sport,
      sportsbook: config.sportsbook,
      apiBase: config.apiBase ?? DEFAULT_API_BASE,
      theme: resolveTheme(el, config),
      labels: config.labels,
      defaultTab: config.defaultTab,
      subAffiliate: config.subAffiliate,
      world: this.world,
      onEmpty: () => {
        el.style.display = 'none'
        el.setAttribute('data-asd-empty', 'true')
      },
    }))

    const instance: ApprovalMapWidgetInstance = {
      destroy: () => {
        root.unmount()
        this.instances.delete(el)
      },
    }
    this.instances.set(el, { root, mountPoint })
    return instance
  }
}

declare global {
  interface Window {
    ApprovalMapWidget: ApprovalMapWidgetSDK
  }
}

/** Builds the SDK and publishes it on `window` under the shared global name. */
export function createApprovalMapSdk(world?: WorldData): ApprovalMapWidgetSDK {
  const sdk = new ApprovalMapWidgetSDK(world)
  if (typeof window !== 'undefined') {
    window.ApprovalMapWidget = sdk
  }
  return sdk
}
